import { useEffect } from "react";
import { buildCanonical } from "@/lib/canonical";
import { isSeoManagedPath } from "@/seo/paths";

interface PageMeta {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
}

const upsertMeta = (selector: string, attr: "name" | "property", key: string, content: string) => {
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
};

const usePageMeta = ({ title, description, canonical, ogImage, ogType = "website", noindex = false }: PageMeta) => {
  useEffect(() => {
    // Routes in the SEO manifest are owned by <SeoHead /> (same tags as the prerendered HTML).
    if (isSeoManagedPath(window.location.pathname)) return;
    document.title = title;

    upsertMeta('meta[name="description"]', "name", "description", description);

    // Robots indexing directive
    upsertMeta('meta[name="robots"]', "name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    // Open Graph
    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    upsertMeta('meta[property="og:type"]', "property", "og:type", ogType);
    if (canonical) {
      upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);
    }
    if (ogImage) {
      upsertMeta('meta[property="og:image"]', "property", "og:image", ogImage);
    }

    // Twitter
    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    if (ogImage) {
      upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", ogImage);
    }

    // Canonical — use the explicit value when provided, otherwise derive a
    // normalized self-referential canonical from the current path.
    const canonicalUrl = canonical ?? buildCanonical(window.location.pathname);
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkCanonical) {
      linkCanonical = document.createElement("link");
      linkCanonical.setAttribute("rel", "canonical");
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute("href", canonicalUrl);
    // Mark that a page owns the canonical for this path so the global
    // CanonicalManager fallback does not override it.
    linkCanonical.dataset.canonicalSource = "page";
    linkCanonical.dataset.canonicalPath = window.location.pathname;
  }, [title, description, canonical, ogImage, ogType, noindex]);
};

export default usePageMeta;
